import FormCompanyImage from 'components/form/FormCompanyImage';
import config from 'config/appConfig';
import { DivConst } from 'constants/divConst';
import { useAppState } from 'hooks/useAppState';
import { useState, type JSX } from 'react';
import { useFormContext } from 'react-hook-form';
import type { DiagnosisResponseType } from 'types/api/diagnosisResponseType';
import type { FormResponseType } from 'types/api/formResponseType';
import type { QuizResponseType } from 'types/api/quizResponseType';
import type { VoiceChatResponseType } from 'types/api/voiceChatResponseType';
import type { DiagnosisEditFormStateType } from 'types/page/diagnosisEditFormStateType';
import type { FormEditFormStateType } from 'types/page/formEditFormStateType';
import type { QuizEditFormStateType } from 'types/page/quizEditFormStateType';
import type { VoiceChatEditFormStateType } from 'types/page/voiceChatEditFormStateType';

/**
 * コンテンツ作成編集 ポップアップ埋め込みタグコンポーネント
 */
export default function WsContentEditPopupEmbed(props: {
  readOnly: boolean;
  quizState?: QuizResponseType | null;
  diagnosisState?: DiagnosisResponseType | null;
  formState?: FormResponseType | null;
  voiceChatState?: VoiceChatResponseType | null;
}): JSX.Element {
  /* ----------------------------------------------------------------------- */
  /* 変数定義 */
  /* ----------------------------------------------------------------------- */
  let contentType: 'quiz' | 'diagnosis' | 'form' | 'voice_chat';
  if (props.quizState) {
    contentType = 'quiz';
  } else if (props.diagnosisState) {
    contentType = 'diagnosis';
  } else if (props.formState) {
    contentType = 'form';
  } else if (props.voiceChatState) {
    contentType = 'voice_chat';
  }
  const state = props.quizState || props.diagnosisState || props.formState || props.voiceChatState;

  const formContext = useFormContext<
    | QuizEditFormStateType
    | DiagnosisEditFormStateType
    | FormEditFormStateType
    | VoiceChatEditFormStateType
  >();
  const { control } = formContext;
  const [copied, setCopied] = useState(false);
  const { noticeMessageSuccess } = useAppState();

  const contentCd = state?.cd || null;
  const publishedPopupImageKey = state?.popupImageKey || null;

  /* ----------------------------------------------------------------------- */
  /* イベントハンドラ関数定義 */
  /* ----------------------------------------------------------------------- */
  const onCopyCode = async () => {
    const embedCode = _generateEmbedCode();
    if (!embedCode) return;
    try {
      await navigator.clipboard.writeText(embedCode);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      noticeMessageSuccess('コードをコピーしました。');
    } catch (err) {
      console.error('コピーに失敗しました:', err);
    }
  };

  /* ----------------------------------------------------------------------- */
  /* プライベート関数定義 */
  /* ----------------------------------------------------------------------- */
  /**
   * 公開情報の表示可否を返す
   */
  const _isVisiblePublishInfo = (): boolean => {
    if (!state) {
      return false;
    }

    // コンテンツタイプに応じたステータスを取得
    let statusDiv: number | null = null;
    if (props.quizState) {
      statusDiv = props.quizState.quizStatusDiv;
    } else if (props.diagnosisState) {
      statusDiv = props.diagnosisState.diagnosisStatusDiv;
    } else if (props.formState) {
      statusDiv = props.formState.formStatusDiv;
    } else if (props.voiceChatState) {
      statusDiv = props.voiceChatState.voiceChatStatusDiv;
    }

    // 掲載(予約)ステータスの場合は表示
    if (statusDiv === DivConst.items.formStatus.reserved.value) {
      return true;
    }

    // 一度でも掲載を迎えたコンテンツの場合は表示
    if (state.publishedAt != null) {
      return true;
    }

    return false;
  };

  const _generateEmbedCode = (): string => {
    if (!contentCd) {
      return '';
    }

    if (!publishedPopupImageKey) {
      return '';
    }

    // 公開情報の表示条件を満たしている場合のみ生成
    if (!_isVisiblePublishInfo()) {
      return '';
    }

    const staticEndpoint = config.s3.bucket.staticContents.endpoint.replace(/\/$/, '');
    const userWebUrl = config.url.userWeb.replace(/\/$/, '');
    const popupJsUrl = `${staticEndpoint}/common/popup/branq-popup.js`;
    const cfgJson = JSON.stringify({ staticEndpoint, userWebUrl });

    return [
      '<!-- BranQ Popup Embed Tag -->',
      '<script>',
      '(function branqPopupBootstrap(w,d,s,u,c,e,t){',
      'c=w.BranqPopup=w.BranqPopup||function(){(c.q=c.q||[]).push(arguments)};',
      'c.q=c.q||[];',
      `c.cfg=${cfgJson};`,
      'e=d.createElement(s);e.async=1;e.src=u;',
      't=d.getElementsByTagName(s)[0];t.parentNode.insertBefore(e,t);',
      `})(window,document,'script',${JSON.stringify(popupJsUrl)});`,
      `BranqPopup(${JSON.stringify(contentType)},${JSON.stringify(contentCd)});`,
      '</script>',
    ].join('\n');
  };

  const embedCode = _generateEmbedCode();
  const hasEmbedCode = embedCode !== '';

  /* ----------------------------------------------------------------------- */
  /* JSX */
  /* ----------------------------------------------------------------------- */
  return (
    <section id="popup-embed" className="section-form-default">
      <div className="container">
        <div className="box-common shadow-none">
          <h3 className="title-section title-md">ポップアップ埋め込みタグ</h3>

          <div className="form-default-container">
            <div className="row row-0">
              {/* ポップアップ画像設定 */}
              <div className="col-md col-header">
                <div className="card form-header">
                  <h4 className="title text-center">ポップアップの画像</h4>
                  <FormCompanyImage
                    control={control}
                    name="popupImageKey"
                    readOnly={props.readOnly}
                  />
                </div>
              </div>

              {/* 埋め込みタグ表示 */}
              <div className="col-md col-form">
                <div className="form-group">
                  <p className="mb-3">
                    下のコードをコピーして、HTMLファイルの&lt;/body&gt;タグの直前に追加してください。
                  </p>
                  <div className="position-relative">
                    <textarea
                      className="form-control font-monospace"
                      rows={12}
                      readOnly
                      value={hasEmbedCode ? embedCode : ''}
                      placeholder="画像を選択して公開設定を行うと、ポップアップ埋め込みタグが表示されます。"
                      style={{
                        paddingRight: '50px',
                      }}
                    />
                    <button
                      onClick={onCopyCode}
                      disabled={!hasEmbedCode}
                      className="btn-empty position-absolute"
                      style={{
                        top: '8px',
                        right: '8px',
                        zIndex: 10,
                      }}
                      title={copied ? 'コピーしました' : 'コピー'}
                    >
                      {copied ? (
                        <img src="/img/icon/copied.svg" alt="コピー済み" />
                      ) : (
                        <img src="/img/icon/copy.svg" alt="コピー" />
                      )}
                    </button>
                  </div>
                  <p className="mt-2 text-muted small">
                    ※ポップアップはコンテンツ公開時のみ表示されます。
                    <br />
                    ※画像を変更した場合にタグの再発行・埋め込みは不要です。
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
